/**
 * Effective rarity colors: the tenant's `theme.rarity` override where set, the
 * tier default otherwise — always normalized to `#rrggbb`.
 *
 * Normalization is the point, not a nicety. `lootThemeSchema.rarity` accepts any
 * string, and `"r,g,b"` triplets are a documented, tested input form (the opener
 * takes them for its glow palette). Renderers concatenate an alpha suffix onto
 * these values (`` `${color}66` ``), which turns a triplet into
 * `"168,85,24766"` — an invalid color, so the browser drops the whole
 * declaration and the element loses its border entirely. Returning hex here is
 * what makes that concatenation safe at every call site.
 *
 * Parsing mirrors `resolveTheme`'s `toRgbTriplet` / `tripletToHex` in
 * @loot-os/react, so the odds table and the spinner rails agree on a tier.
 */
import { STOREFRONT_RARITY_TIERS, type StorefrontRarityKey } from "./rarityTiers.js";

const TRIPLET = /^\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}$/;
const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const RGB_FUNC = /^rgba?\(\s*(\d{1,3})\s*[, ]\s*(\d{1,3})\s*[, ]\s*(\d{1,3})/i;

function channel(value: number): string {
  return Math.max(0, Math.min(255, value)).toString(16).padStart(2, "0");
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

/** A tenant color in any accepted form → `#rrggbb`, or null when unparseable. */
function toHex(color: string): string | null {
  const trimmed = color.trim();

  const hex = HEX.exec(trimmed)?.[1];
  if (hex) {
    const full = hex.length === 3 ? hex.replace(/./g, (c) => c + c) : hex;
    return `#${full.toLowerCase()}`;
  }

  if (TRIPLET.test(trimmed)) {
    const [r = 0, g = 0, b = 0] = trimmed.split(",").map((part) => Number.parseInt(part, 10));
    return rgbToHex(r, g, b);
  }

  const rgb = RGB_FUNC.exec(trimmed);
  if (rgb) {
    return rgbToHex(Number(rgb[1]), Number(rgb[2]), Number(rgb[3]));
  }

  return null;
}

/** One tier's label and effective color. */
export function resolveRarityColor(
  rarity: number,
  overrides?: Partial<Record<StorefrontRarityKey, string>> | null,
): { label: string; color: string } {
  const tier =
    STOREFRONT_RARITY_TIERS.find((t) => t.key === String(rarity)) ?? STOREFRONT_RARITY_TIERS[0];
  const override = overrides?.[tier.key];
  return { label: tier.label, color: (override && toHex(override)) || tier.default };
}

/** Every tier's label and effective color, in tier order. */
export function resolveStorefrontRarityColors(
  overrides?: Partial<Record<StorefrontRarityKey, string>> | null,
): { key: StorefrontRarityKey; label: string; default: string; color: string }[] {
  return STOREFRONT_RARITY_TIERS.map((tier) => ({
    key: tier.key,
    label: tier.label,
    default: tier.default,
    color: (overrides?.[tier.key] && toHex(overrides[tier.key] as string)) || tier.default,
  }));
}
